// The projection screen's arithmetic and vocabulary, kept apart from React.
//
// Everything here is pure: the page hands it the report and the reader's
// choices, and gets back what to draw. The rule every function keeps is the
// screen's whole point: ACTUAL (issued invoices), CONTRACTED (terms on file,
// not yet invoiced) and ESTIMATE (no contract yet) are three different kinds
// of money. Nothing here adds an estimate into either of the other two, and
// the one figure that does combine them says so in its own name.
//
// No money is divided here. Every amount stays integer minor units in the
// report's currency until `formatMoney` reads it.

import type {
  Currency,
  ProjectionAssumptions,
  ProjectionReport,
  ProjectionReportRow,
  ProjectionSettings,
  ProjectionSettingsUpdate,
  ReportScope,
} from "@crm/shared";
import type { ChartSeries } from "../../design/chart/MonthlyChart.js";
import type { QueryParamValue, QueryParams } from "../../api/queryString.js";

// ---------------------------------------------------------------------------
// The scenario: the six assumptions, and the ones this reading overrides.
// ---------------------------------------------------------------------------

/** The assumptions in the order the scenario panel shows them. */
export const ASSUMPTION_KEYS = [
  "go_lives_per_month",
  "monthly_growth_pct",
  "margin_pct",
  "assumed_split_pct",
  "implementation_lead_months",
  "implementation_kickoff_pct",
] as const satisfies readonly (keyof ProjectionAssumptions)[];

export type AssumptionKey = (typeof ASSUMPTION_KEYS)[number];

/** A what-if for one reading: only the assumptions that differ from the saved ones. */
export type ScenarioOverrides = Partial<Record<AssumptionKey, number>>;

export interface AssumptionField {
  label: string;
  suffix: string;
  /** What the number does, in one line. */
  hint: string;
  min: number;
  max: number;
  /** The minimum itself is refused (go-lives per month must be above zero). */
  exclusiveMin: boolean;
  integer: boolean;
}

/** The bounds are the contract's (`ProjectionSettingsUpdate`), stated once here for the inputs. */
export const ASSUMPTION_FIELDS: Record<AssumptionKey, AssumptionField> = {
  go_lives_per_month: {
    label: "Go-lives / month",
    suffix: "deals",
    hint: "Expected go-lives the first projected month can take.",
    min: 0,
    max: 1000,
    exclusiveMin: true,
    integer: false,
  },
  monthly_growth_pct: {
    label: "Monthly growth",
    suffix: "%",
    hint: "How fast that capacity compounds, month on month.",
    min: -50,
    max: 100,
    exclusiveMin: false,
    integer: false,
  },
  margin_pct: {
    label: "Margin",
    suffix: "%",
    hint: "Assumed on our share of estimated revenue only.",
    min: -100,
    max: 100,
    exclusiveMin: false,
    integer: false,
  },
  assumed_split_pct: {
    label: "Assumed share",
    suffix: "%",
    hint: "Our share when neither the deal nor its agreement states one.",
    min: 0,
    max: 100,
    exclusiveMin: false,
    integer: false,
  },
  implementation_lead_months: {
    label: "Implementation lead",
    suffix: "months",
    hint: "Kickoff this many months before go-live.",
    min: 0,
    max: 36,
    exclusiveMin: false,
    integer: true,
  },
  implementation_kickoff_pct: {
    label: "Billed at kickoff",
    suffix: "%",
    hint: "Of the implementation fee; the rest lands at go-live.",
    min: 0,
    max: 100,
    exclusiveMin: false,
    integer: false,
  },
};

/** Why a value cannot be used for an assumption, or null when it can. */
export function assumptionProblem(key: AssumptionKey, value: number): string | null {
  const field = ASSUMPTION_FIELDS[key];
  if (!Number.isFinite(value)) return "Enter a number.";
  if (field.integer && !Number.isInteger(value)) return "Enter a whole number.";
  if (field.exclusiveMin ? value <= field.min : value < field.min) {
    return field.exclusiveMin ? `Must be above ${field.min}.` : `At least ${field.min}.`;
  }
  if (value > field.max) return `At most ${field.max}.`;
  return null;
}

export type ParsedAssumption = { ok: true; value: number } | { ok: false; message: string };

/** Typed text -> an assumption, refusing anything the report would refuse. */
export function parseAssumption(key: AssumptionKey, text: string): ParsedAssumption {
  const trimmed = text.trim();
  if (trimmed === "" || !/^-?\d*(?:\.\d*)?$/.test(trimmed) || trimmed === "-" || trimmed === ".") {
    return { ok: false, message: "Enter a number." };
  }
  const value = Number(trimmed);
  const problem = assumptionProblem(key, value);
  return problem === null ? { ok: true, value } : { ok: false, message: problem };
}

/** The saved assumptions with this reading's overrides on top. */
export function effectiveAssumptions(
  saved: ProjectionAssumptions,
  overrides: ScenarioOverrides,
): ProjectionAssumptions {
  const next: ProjectionAssumptions = { ...saved };
  for (const key of ASSUMPTION_KEYS) {
    const value = overrides[key];
    if (value !== undefined) next[key] = value;
  }
  return next;
}

/**
 * The overrides that still say something: a value equal to the saved one is
 * not a what-if, and keeping it in the URL would leave "Reset to saved" lit
 * for a scenario identical to the default.
 */
export function meaningfulOverrides(
  overrides: ScenarioOverrides,
  saved: ProjectionAssumptions | null,
): ScenarioOverrides {
  const kept: ScenarioOverrides = {};
  for (const key of ASSUMPTION_KEYS) {
    const value = overrides[key];
    if (value === undefined) continue;
    if (saved !== null && saved[key] === value) continue;
    kept[key] = value;
  }
  return kept;
}

export function hasOverrides(overrides: ScenarioOverrides): boolean {
  return ASSUMPTION_KEYS.some((key) => overrides[key] !== undefined);
}

/** The PATCH body that makes this scenario the saved default: changed fields only. */
export function settingsUpdateFor(
  overrides: ScenarioOverrides,
  saved: ProjectionSettings,
): ProjectionSettingsUpdate {
  const body: ProjectionSettingsUpdate = {};
  for (const key of ASSUMPTION_KEYS) {
    const value = overrides[key];
    if (value !== undefined && value !== saved[key]) body[key] = value;
  }
  return body;
}

// ---------------------------------------------------------------------------
// The query.
// ---------------------------------------------------------------------------

export interface ProjectionQueryState {
  currency: Currency;
  /** Null means every partner. */
  partnerId: string | null;
  scope: ReportScope;
  /** `YYYY-MM`. Null leaves it to the report: this month. */
  fromMonth: string | null;
  /** `YYYY-MM`. Null leaves it to the report: two years from the start. */
  toMonth: string | null;
  overrides: ScenarioOverrides;
}

export const DEFAULT_PROJECTION_QUERY: ProjectionQueryState = {
  currency: "CAD",
  partnerId: null,
  scope: "rolled_up",
  fromMonth: null,
  toMonth: null,
  overrides: {},
};

export function projectionQueryParams(state: ProjectionQueryState): QueryParams {
  const params: Record<string, QueryParamValue> = {
    currency: state.currency,
    partner_id: state.partnerId,
    // Meaningless without a partner, so it is left off rather than sent.
    scope: state.partnerId === null ? null : state.scope,
    from_month: state.fromMonth,
    to_month: state.toMonth,
  };
  for (const key of ASSUMPTION_KEYS) {
    const value = state.overrides[key];
    if (value !== undefined) params[key] = value;
  }
  return params;
}

// ---------------------------------------------------------------------------
// The figures.
// ---------------------------------------------------------------------------

/** Which revenue the chart and tiles show: all of it, or the part that reaches us. */
export type Measure = "gross" | "our_share";

export const MEASURE_LABEL: Record<Measure, string> = {
  gross: "Gross",
  our_share: "Our share",
};

interface RowFigures {
  actual: number;
  contracted: number;
  estimateWeighted: number;
  estimateUnweighted: number;
}

function figuresOf(
  part: Pick<ProjectionReportRow, "actual" | "contracted" | "estimate">,
  measure: Measure,
): RowFigures {
  return measure === "gross"
    ? {
        actual: part.actual.gross_minor,
        contracted: part.contracted.gross_minor,
        estimateWeighted: part.estimate.weighted_gross_minor,
        estimateUnweighted: part.estimate.gross_minor,
      }
    : {
        actual: part.actual.our_share_minor,
        contracted: part.contracted.our_share_minor,
        estimateWeighted: part.estimate.weighted_our_share_minor,
        estimateUnweighted: part.estimate.our_share_minor,
      };
}

export const SERIES_LABEL = {
  actual: "Actual (invoiced)",
  contracted: "Contracted (not yet invoiced)",
  estimate: "Estimate, weighted (no contract)",
} as const;

/**
 * Three stacked bars per month: actual and contracted solid, the weighted
 * estimate hatched on top. Stacked rather than side by side so the height is
 * "what the month could come to", and hatched so nobody reads the top segment
 * as money already agreed.
 */
export function chartSeries(rows: readonly ProjectionReportRow[], measure: Measure): ChartSeries[] {
  const figures = rows.map((row) => figuresOf(row, measure));
  return [
    {
      key: "actual",
      label: SERIES_LABEL.actual,
      kind: "bar",
      slot: 1,
      values: figures.map((f) => f.actual),
    },
    {
      key: "contracted",
      label: SERIES_LABEL.contracted,
      kind: "bar",
      slot: 2,
      values: figures.map((f) => f.contracted),
    },
    {
      key: "estimate",
      label: SERIES_LABEL.estimate,
      kind: "bar",
      slot: 3,
      hatched: true,
      values: figures.map((f) => f.estimateWeighted),
    },
  ];
}

/** Per-month caveats the chart marks: metered lines a future month cannot price yet. */
export function chartCaveats(rows: readonly ProjectionReportRow[]): (string | null)[] {
  return rows.map((row) => {
    const parts: string[] = [];
    const unpriced = row.contracted.unpriced_line_count;
    if (unpriced > 0) {
      parts.push(
        `${unpriced} contract line${unpriced === 1 ? "" : "s"} unpriced (usage not known yet), left out`,
      );
    }
    const assumed = row.estimate.assumed_share_deal_count;
    if (assumed > 0) {
      parts.push(`${assumed} estimate${assumed === 1 ? "" : "s"} on the assumed share`);
    }
    return parts.length === 0 ? null : parts.join("; ");
  });
}

export interface WindowFigures {
  actual: number;
  contracted: number;
  /** Actual + contracted: real money only, never an estimate in it. */
  real: number;
  estimateWeighted: number;
  estimateUnweighted: number;
  /** Margin is assumed on our share, whatever the measure. */
  marginWeighted: number;
  marginUnweighted: number;
  expectedGoLives: number;
  unpricedLineCount: number;
  assumedShareDealCount: number;
}

export function windowFigures(report: ProjectionReport, measure: Measure): WindowFigures {
  const f = figuresOf(report.totals, measure);
  return {
    actual: f.actual,
    contracted: f.contracted,
    real: f.actual + f.contracted,
    estimateWeighted: f.estimateWeighted,
    estimateUnweighted: f.estimateUnweighted,
    marginWeighted: report.totals.estimate.weighted_margin_minor,
    marginUnweighted: report.totals.estimate.margin_minor,
    expectedGoLives: report.totals.expected_go_lives,
    unpricedLineCount: report.totals.contracted.unpriced_line_count,
    assumedShareDealCount: report.totals.estimate.assumed_share_deal_count,
  };
}

// ---------------------------------------------------------------------------
// Words.
// ---------------------------------------------------------------------------

/** The always-visible line on how an estimate gets a month. */
export function schedulingSentence(
  a: ProjectionAssumptions,
  formatPct: (pct: number) => string,
): string {
  const growth =
    a.monthly_growth_pct === 0
      ? "held flat"
      : `growing ${formatPct(a.monthly_growth_pct)} a month, compounding`;
  return (
    `How estimates are scheduled: a deal with no contract goes live in the month somebody set, ` +
    `or else in the first month with go-live capacity left — ${a.go_lives_per_month} expected go-lives ` +
    `in the first month, ${growth}. Deals queue furthest stage first, and each takes its win ` +
    `probability of a slot. Its monthly estimate bills from go-live; ${formatPct(a.implementation_kickoff_pct)} ` +
    `of its implementation estimate lands at kickoff, ${a.implementation_lead_months} month` +
    `${a.implementation_lead_months === 1 ? "" : "s"} earlier, and the rest at go-live.`
  );
}
